import React, { Fragment, useEffect, useState } from 'react'
import { Button, CircularProgress, Grid, Typography } from '@mui/material'
import { GenericTable } from '../../_EOS/Table/GenericTable'
import { TableHeaderConfigWithType } from '../../_EOS/Table'
import { usePagination } from '../../../hooks/usePagination'
import { useApi } from '../../../hooks/useApi'
import { ApiResponse } from '../../../typings/ApiResponse'
import { getPickingRouteRelated } from '../api'
import { Status } from '../../../hooks/useFetchState'
import { ListItemInfo } from '../../Picking/ListItemInfo'
import { ePickingRouteStates } from '../api/types'
import { Loading } from '../../UI'

export interface PickingRoutesRelated {
  pickingRouteId: number
  lpn: string
  pickingRouteStateId: number
  validationStatusId: number
  blockValidation: boolean
}

interface PickingRouteRelatedDialogInfoProps {
  pickingRouteId: number
  allRoutesPickingFinalize: boolean
  closeModal: () => void
  executeStartPickingValidate: (pickingRouteId?: string) => void
  requestPickingRouteByLpn: (lpn: string) => void
  referenceId?: string
  lpn?: string
  openModalZone: (refenceId: string) => void
}

export const TableHeaderConfig = () => {
  const table: TableHeaderConfigWithType<PickingRoutesRelated>[] = [
    {
      field: 'pickingRouteId',
      title: 'Ruta de Picking',
      sortable: false
    },
    { field: 'lpn', title: 'LPN', sortable: false },
    { field: 'zoneName', title: 'Zona', sortable: false },
    {
      field: 'pickingRouteStateId',
      title: 'Estado de la ruta',
      formater: (_pickingRouteId: number | string, row) =>
        renderPickingRouteStateId(row!)
    }
  ]

  return table
}

const renderPickingRouteStateId = (
  pickingrouterelated: PickingRoutesRelated
) => {
  const { validationStatusId, pickingRouteStateId } = pickingrouterelated
  const enValidacion = 2
  const validada = 3

  if (validationStatusId !== validada) {
    switch (pickingRouteStateId) {
      case ePickingRouteStates.Creada:
        return <Typography>Creada</Typography>
      case ePickingRouteStates.Iniciada:
        return <Typography>Picking Iniciado</Typography>
      case ePickingRouteStates.Finalizada:
        return <Typography>Picking Finalizado</Typography>
      default:
        break
    }
  }

  if (pickingRouteStateId === ePickingRouteStates.Finalizada) {
    switch (validationStatusId) {
      case enValidacion:
        return <Typography>En validación</Typography>
      case validada:
        return <Typography>Validada</Typography>
    }
  }

  return <Typography>Sin estado</Typography>
}

export const PickingRouteRelatedDialogInfo: React.FC<
  PickingRouteRelatedDialogInfoProps
> = ({
  pickingRouteId,
  allRoutesPickingFinalize,
  closeModal,
  executeStartPickingValidate,
  requestPickingRouteByLpn,
  referenceId,
  lpn,
  openModalZone
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const { state, callApi } = useApi<ApiResponse<PickingRoutesRelated>>()
  const configsHeaders = TableHeaderConfig()
  const pagination = usePagination(10)

  const requestPickingRouteRelated = (page?: number, pageSize?: number) => {
    setLoading(true)
    callApi(
      getPickingRouteRelated(pickingRouteId, page, pageSize, referenceId, lpn)
    )
      .catch((error) => {
        console.log(error)
      })
      .finally(() => setLoading(false))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestPickingRouteRelated(auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    if (!referenceId && !pickingRouteId && !lpn) return
    pagination.setPagination(0, pagination.pageSize)
    requestPickingRouteRelated(0, pagination.pageSize)
  }, [referenceId, pickingRouteId, lpn])

  // Cierra el modal si la carga falla — el padre maneja el mensaje de error
  useEffect(() => {
    if (state.status === Status.Error) {
      closeModal()
    }
  }, [state.status])

  if (state.status !== Status.Success) {
    return <Loading />
  }

  const blockValidation = state.data?.items?.[0]?.blockValidation ?? false

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={12}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Typography variant='h1' style={{ fontWeight: 'bold' }}>
            <ListItemInfo
              primary={
                blockValidation
                  ? 'No puedes validar esta ruta'
                  : 'Rutas relacionadas pendientes'
              }
              secondary=''
            />
          </Typography>
        </Grid>

        <Grid
          item
          justify='flex-start'
          xs={12}
          md={12}
          style={{ display: 'flex', paddingLeft: '30px', paddingRight: '30px' }}
        >
          <Typography variant='body1' style={{ fontWeight: 'bold' }}>
            {blockValidation
              ? 'Ya que cuenta con rutas relacionadas las cuales están pendientes de picking'
              : 'Hay rutas de picking relacionadas pendientes. ¿Deseas continuar con la validación de todas formas?'}
          </Typography>
        </Grid>

        <Grid
          justify='flex-start'
          item
          xs={12}
          md={12}
          style={{ display: 'flex', paddingLeft: '30px', paddingRight: '30px' }}
        >
          <Typography variant='body1' style={{ fontWeight: 'bold' }}>
            Rutas Relacionadas:
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {(state.data?.items === undefined || state.data?.items.length === 0) && !loading && (
            <Typography variant='h6'>Sin rutas Relacionadas</Typography>
          )}
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
          <GenericTable<PickingRoutesRelated>
            dense={true}
            headersConfig={configsHeaders}
            IDKey={'pickingRouteId'}
            data={state.data?.items}
            pagination={{
              totalItem: state.data?.totalItems || 0,
              page: pagination.page,
              pageSize: pagination.pageSize
            }}
            onPaginationChange={handlePaginationChange}
            isLoading={state.status === Status.Loading}
          />
        </Grid>
      </Grid>

      {!allRoutesPickingFinalize && (
        blockValidation ? (
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                type='button'
                variant='contained'
                color='primary'
                onClick={() => closeModal()}
              >
                Aceptar
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                disabled={loading}
                variant='contained'
                style={{ color: 'white', background: 'green' }}
                onClick={() => closeModal()}
              >
                Regresar a la validación
              </Button>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color='inherit' /> : null}
                variant='contained'
                color='primary'
                onClick={() => {
                  setLoading(true)
                  if (referenceId) {
                    openModalZone(referenceId)
                    closeModal()
                  } else if (lpn) {
                    requestPickingRouteByLpn(lpn)
                  } else executeStartPickingValidate(pickingRouteId.toString())
                }}
              >
                Deseo continuar con la validación
              </Button>
            </Grid>
          </Grid>
        )
      )}
    </Fragment>
  )
}
